import {
  ArrowRightIcon,
  BanknotesIcon,
  BoltIcon,
  EyeIcon,
  Squares2X2Icon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { useMutation } from '@tanstack/react-query';
import classNames from 'classnames';
import clsx from 'clsx';
import { stringify } from 'query-string';
import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import * as Yup from 'yup';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { useToast } from '@dashboard/context/ToastContext';
import useSellerQueries from '@dashboard/hooks/queries/useSellerQueries';
import useUrlQuery from '@dashboard/hooks/useUrlQuery';
import StripePartner from '@dashboard/icons/StripePartnerIcon';
import { tracking, useTracking } from '@dashboard/lib/tracking';
import { rootDomain } from '@dashboard/services/env';
import getStripeConnectUrl from '@dashboard/services/getStripeConnectUrl';
import { fetcher } from '@dashboard/services/request';

import Button from '@dashboard/components/Button';
import CFVideoEmbed from '@dashboard/components/CFVideoEmbed';
import { TextInput } from '@dashboard/components/Formik';
import Form from '@dashboard/components/Formik/Form';
import Link from '@dashboard/components/Link';
import Logo from '@dashboard/components/Logo';

const steps = [
  {
    number: 1,
    name: 'Set up your store',
    description: 'Add your store details to get started',
    icon: Squares2X2Icon,
    href: '?step=1',
  },
  {
    number: 2,
    name: 'Add your branding',
    description: 'Logo, color and store URL',
    icon: EyeIcon,
    href: '?step=2',
  },
  {
    number: 3,
    name: 'Connect with Stripe',
    description: 'Secure payment processing',
    icon: BanknotesIcon,
    href: '?step=3',
  },
  {
    number: 4,
    name: 'Invite team members',
    description: 'Work together with your team members',
    icon: UserGroupIcon,
    href: '?step=3',
  },
  {
    number: 5,
    name: 'Welcome onboard',
    description: "We're excited to help you sell online",
    icon: BoltIcon,
    href: '?step=4',
  },
];

const SellerStart = () => {
  useTracking();
  const { seller, isAuthenticating } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(
    parseInt(new URLSearchParams(location.search).get('step')) || 1,
  );
  const urlQuery = useUrlQuery();

  useEffect(() => {
    tracking.trackPageview();
  }, [location]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const step = parseInt(queryParams.get('step'));
    // get all other query params, remove step
    const otherQueryParams = queryParams.toString().replace(`step=${step}&`, '');

    if (step > steps.length + 1) {
      setCurrentStep(1);
      history.replace(`/seller/start?step=1&${otherQueryParams}`);
    } else {
      if (!step || step < 1) {
        setCurrentStep(1);
      } else {
        setCurrentStep(step);
      }
    }
  }, [location, seller]);

  // if landing on step 3 with stripe connected, skip to step 4 (this would happen on Stripe Connect return)
  useEffect(() => {
    const step = parseInt(new URLSearchParams(location.search).get('step'));

    if (step === 3) {
      if (seller.paymentGateways?.stripe?.isActive === true) {
        setCurrentStep(4);
        history.replace('/seller/start?step=4');
      }
    }
  }, [seller._id]);

  function getStepStatus(step) {
    if (step < currentStep) {
      return 'complete';
    } else if (step === currentStep) {
      return 'current';
    } else {
      return 'upcoming';
    }
  }

  function setNextStep(query = {}) {
    setCurrentStep(currentStep + 1);

    history.push({
      search: stringify({
        ...urlQuery,
        ...query,
        step: currentStep + 1,
      }),
    });
  }

  function setStep(step) {
    setCurrentStep(step);
    history.push({
      search: stringify({
        ...urlQuery,
        step: step,
      }),
    });
  }

  return (
    <div className={clsx('min-h-screen relative bg-white')}>
      <div className="absolute top-6 left-4">
        <Logo size={40} />
      </div>

      <div className="flex min-h-screen">
        <div className="hidden lg:block w-96 bg-gray-50 px-4">
          <div className="mt-28">
            <nav className={clsx('max-w-lg lg:max-w-screen-xl relative')} aria-label="Progress">
              <span className="absolute left-6 top-px w-px h-[calc(100%-1px)] bg-gray-200"></span>

              <ol role="list" className={clsx('space-y-8')}>
                {steps.map((step, stepIdx) => (
                  <li key={step.name} className={clsx('relative group')}>
                    <button
                      onClick={() => setStep(step.number)}
                      disabled={
                        getStepStatus(step.number) === 'upcoming' ||
                        getStepStatus(step.number) === 'current'
                      }
                      className="flex gap-x-4 items-center text-left"
                    >
                      <div className="">
                        <div
                          className={clsx('bg-white border rounded-lg p-3', {
                            'border-gray-100': stepIdx + 1 !== currentStep,
                            'group-hover:border-gray-300':
                              getStepStatus(step.number) !== 'upcoming' &&
                              getStepStatus(step.number) !== 'current',
                            'border-chocolate-1000': stepIdx + 1 === currentStep,
                          })}
                        >
                          <step.icon className="h-6 w-6" />
                        </div>
                      </div>

                      <div className="">
                        <div className="font-medium">{step.name}</div>
                        <div className="text-sm text-gray-500">{step.description}</div>
                      </div>
                    </button>
                  </li>
                ))}
              </ol>
            </nav>
          </div>
        </div>

        <div className="max-w-lg mx-auto lg:ml-24 xl:ml-48 flex-1">
          {!isAuthenticating && (
            <div className="mt-28">
              <OnboardingSteps
                currentStep={steps.find(step => step.number === currentStep)}
                setNextStep={setNextStep}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

function OnboardingSteps({ currentStep, setNextStep }) {
  return (
    <div>
      {currentStep.number === 1 && <SetUpStore step={currentStep} setNextStep={setNextStep} />}

      {currentStep.number === 2 && <Branding step={currentStep} setNextStep={setNextStep} />}

      {currentStep.number === 3 && <ConnectStripe step={currentStep} setNextStep={setNextStep} />}

      {currentStep.number === 4 && (
        <InviteTeamMembers step={currentStep} setNextStep={setNextStep} />
      )}

      <div
        className={classNames({
          hidden: currentStep.number !== 5,
        })}
      >
        <WelcomeOnboard step={currentStep} setNextStep={setNextStep} />
      </div>

      {!currentStep.number && <SetUpStore step={currentStep} setNextStep={setNextStep} />}
    </div>
  );
}

function SetUpStore({ step, setNextStep }) {
  const { seller, setSeller, sellerId, token } = useAuth();
  const { updateSeller } = useSellerQueries();
  const { template: createFromTemplateId, page: createFromPageId, ...urlQuery } = useUrlQuery();
  const history = useHistory();

  const createFromTemplateMutation = useMutation({
    mutationFn: values =>
      fetcher(
        `/sellers/${sellerId}/pages/from-template?templateId=${createFromTemplateId}&pageId=${createFromPageId}`,
        token,
        {
          method: 'POST',
          body: JSON.stringify(values),
        },
      ),
    onSuccess: data => {
      history.replace({
        search: stringify({
          ...urlQuery,
          pageId: data.id,
          pageType: data.type,
        }),
      });
    },
  });

  useEffect(() => {
    if (createFromTemplateId || createFromPageId) {
      createFromTemplateMutation.mutate();
    }
  }, [createFromTemplateId, createFromPageId]);

  return (
    <div className="max-w-lg">
      <div className="text-center">
        <h1 className="text-4xl">{step.name}</h1>
        <h3 className="text-xl mt-2 font-normal mb-8">{step.description}</h3>
      </div>

      <Form
        initialValues={{
          displayName: seller.displayName,
          url: seller.url,
          supportEmail: seller.supportEmail,
          defaultCurrency: seller.defaultCurrency,
          industry: seller.industry,
        }}
        fields={[
          {
            name: 'displayName',
            label: 'Store name',
            type: 'text',
            placeholder: 'e.g. My Store',
            defaultValue: seller.displayName,
            required: true,
            increaseMargin: true,
            autoFocus: true,
          },
          {
            name: 'industry',
            label: "What's the main focus of your store?",
            type: 'select',
            defaultValue: seller.industry,
            required: true,
            increaseMargin: true,
            options: [
              {
                label: 'Select an option',
                value: '',
                disabled: true,
                selected: true,
              },
              {
                label: 'Event tickets',
                value: 'event-tickets',
              },
              { label: 'Digital products', value: 'digital-products' },
              { label: 'Physical products', value: 'physical-products' },
              { label: 'Memberships', value: 'memberships' },
              { label: 'Subscriptions', value: 'subscriptions' },
              {
                label: 'Software & SaaS',
                value: 'software-saas',
              },
              { label: 'Services', value: 'services' },
              { label: 'Donations', value: 'donations' },
              { label: 'Other', value: 'other' },
            ],
          },
          {
            name: 'defaultCurrency',
            label: 'Default currency',
            type: 'currency',
            placeholder: 'e.g. USD',
            defaultValue: seller.defaultCurrency,
            required: true,
            increaseMargin: true,
          },
          {
            name: 'email',
            label: 'Support email',
            type: 'text',
            placeholder: 'support@mybusiness.com',
            defaultValue: seller.supportEmail,
            optional: true,
            hideOptional: true,
            increaseMargin: true,
          },
          {
            name: 'url',
            label: 'Website URL',
            type: 'text',
            placeholder: 'e.g. mybusiness.com',
            defaultValue: seller.url,
            optional: true,
            hideOptional: true,
            increaseMargin: true,
          },
        ]}
        submitButton={{
          label: 'Continue',
          appearance: 'primary',
          size: 'full',
          arrowRight: true,
        }}
        showState={false}
        onValuesChange={values => {
          setSeller(values);
        }}
        onSubmit={async values => {
          const { data, error } = await updateSeller(values);

          return {
            data,
            error,
            onSuccess: () => {
              tracking.setUser({
                industry: values.industry,
                storeName: values.displayName,
                websiteUrl: values.url,
              });

              setNextStep();
            },
          };
        }}
      />
    </div>
  );
}

function Branding({ step, setNextStep }) {
  const { seller, mergeSeller } = useAuth();
  const { updateSeller } = useSellerQueries();
  const { pageId } = useUrlQuery();
  const { emitToast } = useToast();

  return (
    <div className="max-w-lg mx-auto">
      <div className="text-center">
        <h1 className="text-4xl">{step.name}</h1>
        <h3 className="text-xl mt-2 font-normal mb-8">{step.description}</h3>
      </div>

      <Form
        initialValues={{
          name: seller.name,
          logo: seller.logo,
          brandColor: seller.brandColor,
        }}
        showState={false}
        onSubmit={async values => {
          const { data, error } = await updateSeller(values);

          return {
            data,
            error,
            onSuccess: () => {
              if (data?.logo?.url) {
                tracking.setUser({
                  $avatar: data.logo.url,
                });
              }

              if (pageId) {
                emitToast({
                  title: 'Checkout created',
                  description: 'Your checkout has been created',
                  type: 'success',
                });

                setNextStep();
              } else {
                setNextStep();
              }
            },
          };
        }}
        validationSchema={Yup.object().shape({
          name: Yup.string().required('Name is required').min(3).max(50),
        })}
        fields={[
          {
            name: 'logo',
            required: false,
            type: 'image',
            label: 'Logo',
            image: seller.logo,
            crop: { height: 512, width: 512, aspect: 1 },
            recommended: 'Recommended size is 512 x 512px',
            onUpdate: async image => {
              mergeSeller({ logo: image });
              await updateSeller({ logo: image });
            },
            increaseMargin: true,
          },
          {
            name: 'brandColor',
            required: false,
            type: 'color-select',
            label: 'Brand color',
            onChange: async color => {
              mergeSeller({ brandColor: color });
              await updateSeller({ brandColor: color });
            },
            increaseMargin: true,
          },
          {
            name: 'name',
            required: true,
            type: 'custom',
            label: 'Store URL',
            renderField: field => (
              <div className="flex w-full">
                <TextInput {...field} required className="rounded-r-none w-full" />
                <div className="bg-gray-50 w-2/3 flex items-center rounded-r-md border border-gray-200 border-l-0 appearance-none py-2 px-2 text-gray-800">
                  .{rootDomain}
                </div>
              </div>
            ),
            increaseMargin: true,
          },
        ]}
        submitButton={{
          label: 'Continue',
          appearance: 'primary',
          size: 'full',
          arrowRight: true,
        }}
      />
    </div>
  );
}

function InviteTeamMembers({ step, setNextStep }) {
  const [invited, setInvited] = useState(false);

  const { seller } = useAuth();

  const { inviteMember } = useSellerQueries();
  const { emitToast } = useToast();

  const invite = useMutation({
    mutationFn: inviteMember,
    onSuccess: (_data, variables) => {
      setInvited(true);

      emitToast({
        title: 'Invite sent!',
        description: `Ask your team member to check their inbox (${variables.email})`,
        type: 'success',
      });
    },
  });

  return (
    <div className="max-w-lg">
      <div className="text-center">
        <h1 className="text-4xl">{step.name}</h1>
        <h3 className="text-xl mt-2 font-normal mb-8">{step.description}</h3>
      </div>

      <div className="mt-8">
        <Form
          initialValues={{ email: '', role: 'admin' }}
          onSubmit={async (values, actions) => {
            try {
              const response = await invite.mutateAsync({
                sellerId: seller._id,
                email: values.email,
              });

              actions.resetForm();

              return {
                data: response.data,
              };
            } catch (err) {
              return {
                error: err.message,
              };
            }
          }}
          fields={[
            {
              name: 'email',
              label: 'Email',
              tags: ['one at a time'],
              type: 'email',
              placeholder: 'Enter email address',
              required: true,
              autoFocus: true,
            },
          ]}
          showState={false}
          submitButton={{
            label: 'Send invite',
            appearance: 'primary',
            size: 'full',
          }}
        />
      </div>

      <div className="mt-4 block text-center">
        {invited && (
          <Button appearance="secondary" size="full" onClick={() => setNextStep()}>
            Continue <ArrowRightIcon className="h-4 w-4 ml-2" />
          </Button>
        )}

        {!invited && (
          <Link variant="secondary" size="small" onClick={() => setNextStep()}>
            Do this later
          </Link>
        )}
      </div>
    </div>
  );
}

function ConnectStripe({ step, setNextStep }) {
  return (
    <div className="max-w-lg">
      <div className="text-center">
        <h1 className="text-4xl">{step.name}</h1>
        <h3 className="text-xl mt-2 font-normal mb-8">{step.description}</h3>
      </div>

      <div className="flex flex-col gap-y-2">
        <div className="bg-chocolate-50 rounded-lg flex items-center justify-center relative h-16">
          <div className="absolute top-2 right-2">
            <CheckCircleIcon className="h-6 w-6 text-chocolate-1000" />
          </div>
          <div>All funds go directly into your Stripe account</div>
        </div>

        <div className="bg-chocolate-50 rounded-lg flex items-center justify-center relative h-16">
          <div className="absolute top-2 right-2">
            <CheckCircleIcon className="h-6 w-6 text-chocolate-1000" />
          </div>
          <div>You are free to disconnect at any time</div>
        </div>

        <div className="bg-chocolate-50 rounded-lg flex items-center justify-center relative h-16">
          <div className="absolute top-2 right-2">
            <CheckCircleIcon className="h-6 w-6 text-chocolate-1000" />
          </div>
          <div>You must connect to accept payments</div>
        </div>
      </div>

      <div className="mt-8">
        <Button appearance="primary" size="full" to={getStripeConnectUrl()} target="_top">
          Connect with Stripe <ArrowRightIcon className="h-4 w-4 ml-2" />
        </Button>
      </div>

      <div className="mt-4 block text-center">
        <button type="button" onClick={() => setNextStep()}>
          <Link variant="secondary" size="small">
            Do this later
          </Link>
        </button>
      </div>

      <small className="mt-8 block text-center">
        If you have another platform connected to your Stripe account, you will be asked to create a
        sub-account.{' '}
        <Link to="/help/articles/connect-with-stripe#create-a-subaccount-on-stripe" target="_blank">
          Learn more
        </Link>
      </small>

      <div className="mt-8 mx-auto">
        <a href="https://stripe.partners/directory/checkout-page" target="_blank" rel="noreferrer">
          <StripePartner />
        </a>
      </div>
    </div>
  );
}

function WelcomeOnboard({ step }) {
  const { user } = useAuth();
  const { pageId, pageType } = useUrlQuery();
  const history = useHistory();

  // track page creation onboarding event
  useEffect(() => {
    if (pageId) {
      tracking.trackEvent('Onboarding: Create page', {
        fromTemplate: true,
        pageType: pageType,
      });
    }
  }, [pageId, pageType]);

  return (
    <div className="max-w-lg">
      <div className="text-center">
        <h1 className="text-4xl">
          {step.name}, {user.name}!
        </h1>
        <h3 className="text-xl mt-2 font-normal mb-8">{step.description}</h3>
      </div>

      <div>
        <CFVideoEmbed id="f24d1b9c7697390951017bc5fd2ed75d" name="Welcome to Checkout Page" />
      </div>

      <div className="flex items-center justify-between gap-x-4 mt-8">
        {pageId && (
          <Button
            appearance="primary"
            size="full"
            onClick={() => {
              tracking.trackEvent('Onboarding: View page edit');
              history.replace('/pages');
              history.push(`/pages/${pageId}/customize`);
            }}
          >
            Customize checkout page <ArrowRightIcon className="h-4 w-4 ml-2" />
          </Button>
        )}

        {!pageId && (
          <Button
            appearance="primary"
            size="full"
            onClick={() => {
              history.replace('/');
              history.push('/');
            }}
          >
            Get started <ArrowRightIcon className="h-4 w-4 ml-2" />
          </Button>
        )}
      </div>
    </div>
  );
}

export default SellerStart;
